import { EditOutlined, LoginOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Form, Input, List, Modal, Switch, Tag, Typography } from 'antd';
import axios from 'axios';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { apiErrorMessage } from '../api/client';
import { businessesApi } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type {
  Business,
  CreateBusinessAdminBody,
  CreateBusinessBody,
  UpdateBusinessBody,
} from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorBlock, LoadingBlock } from '../components/PageStates';
import { useBusinessContext } from '../context/BusinessContext';

interface CreateFormValues {
  name: string;
  adminLogin: string;
  adminName: string;
  adminPassword: string;
}

interface EditFormValues {
  name: string;
  isArchived: boolean;
  moyskladEnabled: boolean;
}

/**
 * Tenant administration (PLATFORM_ADMIN only): every business, create one with
 * its first admin, rename/archive, move the MoySklad account, add a recovery
 * admin, and «enter» a business (which is what the header switcher does too).
 * Mirrors the UsersTab list+modal pattern.
 */
export function BusinessesPage(): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshMe } = useAuth();
  const { setBusiness } = useBusinessContext();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Business | null>(null);
  const [addingAdminTo, setAddingAdminTo] = useState<Business | null>(null);
  const [createForm] = Form.useForm<CreateFormValues>();
  const [editForm] = Form.useForm<EditFormValues>();
  const [adminForm] = Form.useForm<CreateBusinessAdminBody>();

  const query = useQuery({ queryKey: queryKeys.businesses, queryFn: businessesApi.list });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.businesses });
  };

  const errorText = (error: unknown): string => {
    const code = axios.isAxiosError(error)
      ? (error.response?.data as { message?: string } | undefined)?.message
      : undefined;
    const map: Record<string, string> = {
      LOGIN_TAKEN: t('settings.users.loginTaken'),
      BUSINESS_NOT_FOUND: t('businesses.notFound'),
      BUSINESS_ARCHIVED: t('businesses.archivedError'),
    };
    return (code && map[code]) ?? apiErrorMessage(error) ?? t('common.requestError');
  };

  const createMutation = useMutation<Business, Error, CreateFormValues>({
    mutationFn: (values) => {
      const body: CreateBusinessBody = {
        name: values.name.trim(),
        adminLogin: values.adminLogin.trim(),
        adminName: values.adminName.trim(),
        adminPassword: values.adminPassword,
      };
      return businessesApi.create(body);
    },
    onSuccess: () => {
      message.success(t('businesses.created'));
      invalidate();
      setCreating(false);
    },
    onError: (error) => message.error(errorText(error)),
  });

  const updateMutation = useMutation<Business, Error, { id: string; body: UpdateBusinessBody }>({
    mutationFn: ({ id, body }) => businessesApi.update(id, body),
    onSuccess: async (_row, { id, body }) => {
      message.success(t('common.saved'));
      invalidate();
      setEditing(null);
      // The business we are standing in changed shape (name/MoySklad/archived) —
      // re-read our own identity so the shell follows.
      if (id === user?.businessId) {
        if (body.isArchived) await setBusiness(null);
        else await refreshMe();
      }
    },
    onError: (error) => message.error(errorText(error)),
  });

  const adminMutation = useMutation<{ id: string }, Error, CreateBusinessAdminBody>({
    mutationFn: (values) =>
      businessesApi.createAdmin(addingAdminTo!.id, {
        login: values.login.trim(),
        name: values.name.trim(),
        password: values.password,
      }),
    onSuccess: () => {
      message.success(t('businesses.adminAdded'));
      invalidate();
      setAddingAdminTo(null);
    },
    onError: (error) => message.error(errorText(error)),
  });

  const openCreate = (): void => {
    setCreating(true);
    createForm.setFieldsValue({ name: '', adminLogin: '', adminName: '', adminPassword: '' });
  };
  const openEdit = (item: Business): void => {
    setEditing(item);
    editForm.setFieldsValue({
      name: item.name,
      isArchived: item.isArchived,
      moyskladEnabled: item.moyskladEnabled,
    });
  };
  const openAddAdmin = (item: Business): void => {
    setAddingAdminTo(item);
    adminForm.setFieldsValue({ login: '', name: '', password: '' });
  };

  const submitEdit = (values: EditFormValues): void => {
    if (!editing) return;
    const body: UpdateBusinessBody = {
      name: values.name.trim(),
      isArchived: values.isArchived,
      moyskladEnabled: values.moyskladEnabled,
    };
    updateMutation.mutate({ id: editing.id, body });
  };

  const enter = (item: Business): void => {
    void setBusiness(item.id)
      .then(() => navigate('/', { replace: true }))
      .catch((error: unknown) => message.error(errorText(error)));
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Button type="primary" icon={<PlusOutlined />} size="large" onClick={openCreate}>
        {t('businesses.add')}
      </Button>

      {query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : (
        <Card size="small" styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
          <List
            dataSource={query.data}
            renderItem={(item) => (
              <List.Item
                style={{ paddingInline: 4 }}
                extra={
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      icon={<UserAddOutlined />}
                      onClick={() => openAddAdmin(item)}
                      aria-label={t('businesses.addAdmin')}
                      disabled={item.isArchived}
                    />
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => openEdit(item)}
                      aria-label={t('common.edit')}
                    />
                    <Button
                      type={item.id === user?.businessId ? 'default' : 'primary'}
                      icon={<LoginOutlined />}
                      onClick={() => enter(item)}
                      disabled={item.isArchived || item.id === user?.businessId}
                      aria-label={t('businesses.enter')}
                    />
                  </div>
                }
              >
                <List.Item.Meta
                  title={
                    <>
                      {item.name}
                      {item.id === user?.businessId ? (
                        <Tag color="blue" style={{ marginInlineStart: 8 }}>
                          {t('businesses.currentTag')}
                        </Tag>
                      ) : null}
                      {item.moyskladEnabled ? (
                        <Tag color="cyan" style={{ marginInlineStart: 8 }}>
                          {t('businesses.moyskladTag')}
                        </Tag>
                      ) : null}
                      {item.isArchived ? (
                        <Tag style={{ marginInlineStart: 8 }}>{t('common.archivedTag')}</Tag>
                      ) : null}
                    </>
                  }
                  description={
                    <Typography.Text type="secondary">
                      {t('businesses.counts', {
                        users: item.counts.users,
                        stores: item.counts.stores,
                      })}
                    </Typography.Text>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      <Modal
        open={creating}
        onCancel={() => setCreating(false)}
        title={t('businesses.add')}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        confirmLoading={createMutation.isPending}
        onOk={() => createForm.submit()}
        destroyOnClose
      >
        <Form<CreateFormValues>
          form={createForm}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => createMutation.mutate(values)}
        >
          <Form.Item
            name="name"
            label={t('businesses.name')}
            rules={[{ required: true, whitespace: true, message: t('common.nameRequired') }]}
          >
            <Input size="large" maxLength={200} autoComplete="off" />
          </Form.Item>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('businesses.firstAdminHint')}
          </Typography.Text>
          <Form.Item
            name="adminLogin"
            label={t('settings.users.login')}
            rules={[{ required: true, whitespace: true, message: t('settings.users.loginMin') }]}
          >
            <Input size="large" maxLength={64} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="adminName"
            label={t('settings.users.displayName')}
            rules={[{ required: true, whitespace: true, message: t('common.nameRequired') }]}
          >
            <Input size="large" maxLength={200} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="adminPassword"
            label={t('settings.users.password')}
            rules={[
              { required: true, message: t('settings.passwordRequired') },
              { min: 8, message: t('settings.passwordMin') },
            ]}
          >
            <Input.Password size="large" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editing !== null}
        onCancel={() => setEditing(null)}
        title={editing?.name}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={updateMutation.isPending}
        onOk={() => editForm.submit()}
        destroyOnClose
      >
        <Form<EditFormValues>
          form={editForm}
          layout="vertical"
          requiredMark={false}
          onFinish={submitEdit}
        >
          <Form.Item
            name="name"
            label={t('businesses.name')}
            rules={[{ required: true, whitespace: true, message: t('common.nameRequired') }]}
          >
            <Input size="large" maxLength={200} />
          </Form.Item>
          <Form.Item
            name="moyskladEnabled"
            label={t('businesses.moysklad')}
            valuePropName="checked"
            extra={t('businesses.moyskladHint')}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="isArchived"
            label={t('settings.archivedSwitch')}
            valuePropName="checked"
            extra={t('businesses.archivedHint')}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={addingAdminTo !== null}
        onCancel={() => setAddingAdminTo(null)}
        title={t('businesses.addAdminFor', { name: addingAdminTo?.name ?? '' })}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        confirmLoading={adminMutation.isPending}
        onOk={() => adminForm.submit()}
        destroyOnClose
      >
        <Form<CreateBusinessAdminBody>
          form={adminForm}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => adminMutation.mutate(values)}
        >
          <Form.Item
            name="login"
            label={t('settings.users.login')}
            rules={[{ required: true, whitespace: true, message: t('settings.users.loginMin') }]}
          >
            <Input size="large" maxLength={64} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="name"
            label={t('settings.users.displayName')}
            rules={[{ required: true, whitespace: true, message: t('common.nameRequired') }]}
          >
            <Input size="large" maxLength={200} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('settings.users.password')}
            rules={[
              { required: true, message: t('settings.passwordRequired') },
              { min: 8, message: t('settings.passwordMin') },
            ]}
          >
            <Input.Password size="large" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
